// Lib
import React from 'react';

// Utils
import { prop } from '../../../../common/utils/immutableHelper';
import { getElementCardCropInset, getElementCardRenderedWidthPx } from '../../utils/elementUtil';
import { getData, getDataType } from '../../attachments/attachmentsSelector';
import {
    getElementId,
    getFileProp,
    getImageProp,
    getMediaAudioOnly,
} from '../../../../common/elements/utils/elementPropertyUtils';
import { getMainEditorKey } from '../../utils/elementEditorUtils';
import fileCanShowMedia from '../utils/fileCanShowMedia';

// Components
import EditableTitle from '../../../components/editableTitle/EditableTitle';
import DetailViewUntyped from '../../../components/displayMode/DetailView';
import { FilePreviewWithImageHover } from './FilePreview';
import FileIcon from '../../../components/fileIcon/FileIcon';
import FileSecondaryDetails from './FileSecondaryDetails';
import FileProcessingIndicator from './FileProcessingIndicator';

// Constants
import { FILE_TYPES_INFO } from '../../../../common/files/fileConstants';

// Types
import type * as Immutable from 'immutable';
import { ImMNElement } from '../../../../common/elements/elementModelTypes';

// Styles
import './FileDetailViewContent.scss';

// DetailView is an untyped forwardRef JS component; type the props we pass to it.
const DetailView = DetailViewUntyped as React.FC<{
    className?: string;
    previewElement?: React.ReactNode;
    iconElement?: React.ReactNode;
    titleElement?: React.ReactNode;
    secondaryElement?: React.ReactNode;
}>;

type FileDetailViewContentProps = {
    element: ImMNElement;
    attachment?: Immutable.Map<string, unknown>;
    gridSize: number;
    isEditable?: boolean;
    isSingleSelected?: boolean;
    isResizing?: boolean;
    isResizingOrAnimating?: boolean;
    filterQuery?: string;
    showDownloadOption?: boolean;
    shouldDisplayMediaOnly?: boolean;
    inList?: string;
    openElementViewer?: () => void;
};

const FileDetailViewContent: React.FC<FileDetailViewContentProps> = (props) => {
    const {
        element,
        isEditable,
        isSingleSelected,
        isResizingOrAnimating,
        filterQuery,
        attachment,
        showDownloadOption,
        shouldDisplayMediaOnly,
        inList,
        openElementViewer,
    } = props;
    const fileData = getFileProp(element) || getData(attachment);
    const imageData = getImageProp(element);
    const widthPx = getElementCardRenderedWidthPx(props);
    const savedWidthPx = getElementCardRenderedWidthPx({ ...props, tempSize: null });

    const isAttachmentAnImagePreview = getDataType(attachment) === FILE_TYPES_INFO.IMAGE.type;
    const awaitingPreview = !imageData && fileCanShowMedia(element, fileData);

    // Upload and download byte progress are shown by FileTransferToast, so the card only surfaces
    // the server-side processing state.
    const showProgress = !isAttachmentAnImagePreview && awaitingPreview;
    const showMetadata = !shouldDisplayMediaOnly;

    const fallbackTitle = prop('filename', fileData);
    const audioOnly = getMediaAudioOnly(element);

    const editorKey = getMainEditorKey(props);

    const cropInset = getElementCardCropInset(inList);

    return (
        <DetailView
            className="FileDetailViewContent"
            previewElement={
                <FilePreviewWithImageHover
                    {...props}
                    cropToGrid={!isResizingOrAnimating}
                    cropInset={cropInset}
                    widthPx={widthPx}
                    savedWidthPx={savedWidthPx}
                    audioOnly={audioOnly}
                />
            }
            iconElement={showMetadata && <FileIcon fileType={prop('type', fileData)} audioOnly={audioOnly} />}
            titleElement={
                showMetadata && (
                    <EditableTitle
                        element={element}
                        elementId={getElementId(element)}
                        isEditable={isEditable}
                        initialValue={fallbackTitle}
                        maxLength={500}
                        selectFirst
                        selectAllOnEdit
                        isSelected={isSingleSelected}
                        filterQuery={filterQuery}
                        editorKey={editorKey}
                    />
                )
            }
            secondaryElement={
                showMetadata &&
                (showProgress ? (
                    <FileProcessingIndicator />
                ) : (
                    <FileSecondaryDetails
                        element={element}
                        fileData={fileData}
                        showDownloadOption={showDownloadOption}
                        openElementViewer={openElementViewer}
                    />
                ))
            }
        />
    );
};

export default FileDetailViewContent;
